/**
 * Pipeline comercial — constantes de stage compartidas por las páginas de
 * marketing (Pipeline, detalle de oportunidad, detalle de cliente). Sprint 13.
 *
 * PipelineStage es espejo contractual del enum de JR2_OS: 9 valores en
 * MAYÚSCULAS, mismo orden. EN_INVERSION es una etapa ABIERTA entre
 * PROPUESTA_ENVIADA y GANADA (pondera en el forecast).
 *
 * Las líneas de servicio ya NO son enum: son codes String del catálogo
 * ServiceLineDef (GET /v1/marketing/service-lines, read-only — el catálogo se
 * gestiona en JR2_OS). Usa `serviceLineLabel` para resolver el label.
 */

export type PipelineStage =
  | 'LEAD'
  | 'PROSPECTO'
  | 'OPORTUNIDAD'
  | 'PROPUESTA_EN_PREPARACION'
  | 'PROPUESTA_ENVIADA'
  | 'EN_INVERSION'
  | 'GANADA'
  | 'PERDIDA'
  | 'EN_ESPERA'

/** Orden canónico del pipeline (el mismo que el enum de JR2_OS). */
export const STAGE_ORDER: PipelineStage[] = [
  'LEAD',
  'PROSPECTO',
  'OPORTUNIDAD',
  'PROPUESTA_EN_PREPARACION',
  'PROPUESTA_ENVIADA',
  'EN_INVERSION',
  'GANADA',
  'PERDIDA',
  'EN_ESPERA',
]

/** Etiquetas en español para la UI. */
export const STAGE_LABELS: Record<PipelineStage, string> = {
  LEAD: 'Lead',
  PROSPECTO: 'Prospecto',
  OPORTUNIDAD: 'Oportunidad',
  PROPUESTA_EN_PREPARACION: 'Propuesta en preparación',
  PROPUESTA_ENVIADA: 'Propuesta enviada',
  EN_INVERSION: 'En inversión',
  GANADA: 'Ganada',
  PERDIDA: 'Perdida',
  EN_ESPERA: 'En espera',
}

/** Stages terminales (cierran la oportunidad; irreversibles a efectos de funnel). */
export const TERMINAL_STAGES: PipelineStage[] = ['GANADA', 'PERDIDA']

/**
 * Flujo activo reversible (para agrupar en <optgroup> los cambios de stage).
 * EN_INVERSION es abierta: va después de PROPUESTA_ENVIADA.
 */
export const ACTIVE_FLOW_STAGES: PipelineStage[] = [
  'LEAD',
  'PROSPECTO',
  'OPORTUNIDAD',
  'PROPUESTA_EN_PREPARACION',
  'PROPUESTA_ENVIADA',
  'EN_INVERSION',
]

/** Línea de servicio del catálogo ServiceLineDef (mirror de JR2_OS). */
export interface ServiceLine {
  id: string
  code: string
  name: string
  description: string | null
  active: boolean
  position: number
  isDefault: boolean
}

/**
 * Humaniza un code de línea de servicio para no enseñar nunca el code crudo
 * en la UI (Sprint 19): CONSULTORIA_IA → «Consultoria IA». Heurística:
 * palabras de ≤3 letras se tratan como siglas (IA, SEO, CRM) y quedan en
 * mayúsculas; el resto va en title-case. No intenta restituir acentos — el
 * label bueno siempre es el del catálogo; esto es solo el fallback.
 */
export function humanizeServiceLineCode(code: string): string {
  return code
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) =>
      w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(' ')
}

/**
 * Resuelve el label de un code de serviceLine contra el catálogo cargado.
 * Fallback (catálogo aún cargando o línea retirada en JR2_OS): versión
 * humanizada del code vía `humanizeServiceLineCode` — nunca el code crudo.
 */
export function serviceLineLabel(code: string, catalog: ServiceLine[]): string {
  return catalog.find((sl) => sl.code === code)?.name ?? humanizeServiceLineCode(code)
}
